import { config } from "aaa";
import * as _ from "lodash";
import * as platform from "platform";
import * as React from "react";
import { chunk } from "lodash";

export function clamp(value: number, min: number, max: number): number {
    return Math.max(Math.min(value, max), min);
}

export function formatIban(iban?: string) {
    if (!iban) {
        return "";
    }

    return chunk(iban, 4).map(part => part.join("")).join(" ");
}

export function currencyToString(value: number, precision: number): string {
    const divider = Math.pow(10, precision);
    return `${(value / divider).toFixed(2)}`.replace(".", config.CURRENCY_DECIMAL_SEPARATOR);
}

export const getDefaultKeyPressHandler = (callback?: () => void) => (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
        if (callback) {
            callback();
        }
    }
};

/**
 * Returns currency symbol for ISO code
 */
export function currencySymbolForCode(currencyCode: string) {
    const a = 0;
    return a.toLocaleString("en", {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: "symbol",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).replace("0.00", "");
}

/**
 * Given a number 123456.893756  returns "123.456,89 EUR"
 */
export function numberToCurrencyString(value: number, currency = "EUR", integerOnly = false, symbolInFront = true): string {
    let formatted = value.toLocaleString(config.CURRENCY_LOCALE, {
        style: "currency",
        currency: currency,
        currencyDisplay: "symbol",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    if (integerOnly) {
        const parts = formatted.split(config.CURRENCY_DECIMAL_SEPARATOR);
        parts[1] = parts[1].slice(2);
        formatted = parts[0] + parts[1];
    }

    if (symbolInFront) {
        const currencySymbol = currencySymbolForCode(currency);
        const nonbreakingSpace = "\xa0";
        formatted = currencySymbol + nonbreakingSpace + formatted.replace(nonbreakingSpace + currencySymbol, "");
    }

    return formatted;
}

/**
 * Generate a hash from a string
 */
export function stringHash(s: string): number {
    let hash = 0, i, chr;
    if (s.length === 0) {
        return hash;
    }

    for (i = 0; i < s.length; i++) {
        chr = s.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }

    return hash;
}

/**
 * Generate a random color from a string
 */
export function stringToRandomColor(s: string): string {
    const hash = stringHash(s);
    const unsignedHash = hash >>> 0;
    return "#" + unsignedHash.toString(16);
}

/**
 * Check if uid is valid
 * @param uid
 */
export function isUid(uid: string): boolean {
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(uid);
}

export function b64ToUint8(b64Data: string) {
    const byteCharacters = atob(b64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (var i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Uint8Array(byteNumbers);
}

// From https://stackoverflow.com/a/16245768/677910
export function b64ToBlob(b64Data: string, contentType: string, sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);

        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
}

export function isIos(): boolean {
    // Hack from here: https://stackoverflow.com/a/57924983/677910
    const iPadOS = (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    if (iPadOS) {
        return true;
    }

    if (platform.os) {
        return platform.os.family === "iOS";
    } else {
        var iDevices = [
            "iPad Simulator",
            "iPhone Simulator",
            "iPod Simulator",
            "iPad",
            "iPhone",
            "iPod"
        ];

        if (!!navigator.platform) {
            while (iDevices.length) {
                if (navigator.platform === iDevices.pop()) {
                    return true;
                }
            }
        }

        return false;
    }
}

export function isIE(): boolean {
    return platform.name === "IE";
}

export function isEdge(): boolean {
    return platform.name === "Microsoft Edge";
}

// Check if running on android. Optional version parameter.
// Attention: Provide "4." for all android Versions with major version 4
export function isAndroid(version?: string) {
    if (!platform.os) {
        return false;
    }

    let ret = platform.os.family === "Android";
    if (version) {
        ret = ret && _.startsWith(platform.os.version, version);
    }
    return ret;
}

export function isHuawei() {
    return navigator.userAgent.toLowerCase().includes("huawei") || navigator.platform.toLowerCase().includes("huawei");
}

export function supportsAnimatedSvg() {
    return !isIE() && !isEdge() && !isAndroid("4.");
}

export function isChrome(): boolean {
    return platform.name === "Chrome";
}

export function isIPhone() {
    return platform.product === "iPhone";
}

export function isIPad() {
    const iPadOS = (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    if (iPadOS) {
        return true;
    }

    return platform.product === "iPad";
}

export function isWindows(): boolean {
    if (!platform.os) {
        return false;
    }

    return _.startsWith(platform.os.family, "Windows");
}

export function isMac(): boolean {
    if (!platform.os) {
        return false;
    }

    return platform.os.family === "OS X";
}

export function isMobile(): boolean {
    return isAndroid() || isIos();
}

export function safeWindowOpen(url: string, target: string = "_self") {
    // Don't allow javascript in URLs
    if (url.indexOf("javascript:") >= 0) {
        return;
    }

    // Don't allow data URLs
    if (url.indexOf("data:") >= 0) {
        return;
    }

    window.open(url, target);
}

/**
 * Opens an external URL, handling deep links properly in webview contexts.
 * In mobile app contexts, uses location.href for deep links to prevent webview navigation issues.
 * @param url The URL to open
 * @param isMobileApp Whether we're in a mobile app context (webview)
 * @param target The target for window.open (only used in non-mobile contexts)
 */
export function openExternalLink(url: string, isMobileApp: boolean, target: string = "_blank") {
    if (isMobileApp) {
        // Use location.href for deep links in webview to prevent navigation issues
        // This ensures the deep link is handled by the native app without
        // causing the webview to navigate to an invalid URL
        const deepLinkUrl = `georgeapp://at/open_external_link?url=${encodeURIComponent(url)}`;
        window.location.href = deepLinkUrl;
    } else {
        safeWindowOpen(url, target);
    }
}

export function openAsLink(url: string, target: string = "_self") {
    const a = document.createElement("a");
    a.href = url;
    a.target = target;
    document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
    // Sources:
    // https://javascript.info/dispatch-events
    // https://stackoverflow.com/questions/14587917/trigger-click-event-on-link-tag-in-js-for-android-and-iphone
    // https://stackoverflow.com/questions/44432330/programmatically-trigger-click-event-of-button-on-android

    const event = document.createEvent("MouseEvents");
    event.initEvent("click", true, true);
    a.dispatchEvent(event);
    // Alternative way to create the event:
    // const event = new MouseEvent("click", { bubbles: true, cancelable: true });
    // a.dispatchEvent(event);
    a.remove();
}

// Taken from here: https://stackoverflow.com/a/34046084/677910
function copyElementToClipboard(el: any) {
    const viewport = document.querySelector("meta[name=viewport]");
    const oldViewport = viewport?.getAttribute("content");
    if (isIos()) {
        // Fix auto zoom on selection in iOS by setting user-scalable=0
        viewport?.setAttribute("content", "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0");

        const oldContentEditable = el.contentEditable;
        const oldReadOnly = el.readOnly;
        const range = document.createRange();

        el.contentEditable = true;
        el.readOnly = false;
        range.selectNodeContents(el);

        const s = window.getSelection();
        s?.removeAllRanges();
        s?.addRange(range);

        el.setSelectionRange(0, 999999); // A big number, to cover anything that could be inside the element.

        el.contentEditable = oldContentEditable;
        el.readOnly = oldReadOnly;

    } else {
        el.focus();
        el.select();
    }

    try {
        document.execCommand("copy");
    } catch (err) {
        console.error("Copy to clipboard failed", err);
    }

    if (isIos()) {
        viewport?.setAttribute("content", oldViewport || "");
    }
}

export function copyTextToClipboard(text: string) {
    const textArea = document.createElement("textarea");

    textArea.value = text;
    textArea.style.position = "fixed"; // prevents jumping to bottom on mobile if there is a scrollbar
    document.body.appendChild(textArea);
    copyElementToClipboard(textArea);
    document.body.removeChild(textArea);
}

// Helper function because I always forget the regex syntax
export function replaceAll(str: string, search: string, replace: string) {
    return str.replace(new RegExp(search, "g"), replace);
}

export const removeWhitespace = (s: string) => s.replace(/\s/g, "");




// WEBPACK FOOTER //
// ./src/util/helpers.ts